/* eslint-disable */
import type { ClientScenario } from "./api.pb";

export enum SupportContactType {
  EMAIL = "EMAIL",
  CALLBACK = "CALLBACK",
  TEXT = "TEXT",
  CHAT = "CHAT",
}

export enum SupportPermitRequestResult {
  GRANTED = "GRANTED",
  DENIED = "DENIED",
}

export enum SupportPermitDenialReason {
  NOT_ELIGIBLE = "NOT_ELIGIBLE",
  NOT_CURRENTLY_AVAILABLE = "NOT_CURRENTLY_AVAILABLE",
  TOO_BUSY = "TOO_BUSY",
  UPDATE_REQUIRED = "UPDATE_REQUIRED",
}

/** Used for Cash case routing within Salesforce. */
export enum CashRoutingCategory {
  DEFAULT = "DEFAULT",
  BITCOIN = "BITCOIN",
  SEGMENT_A = "SEGMENT_A",
  SEGMENT_B = "SEGMENT_B",
  SEGMENT_C = "SEGMENT_C",
  SEGMENT_D = "SEGMENT_D",
  SEGMENT_E = "SEGMENT_E",
  SEGMENT_F = "SEGMENT_F",
  SEGMENT_G = "SEGMENT_G",
  SEGMENT_H = "SEGMENT_H",
  SEGMENT_I = "SEGMENT_I",
  SEGMENT_J = "SEGMENT_J",
  SEGMENT_K = "SEGMENT_K",
  SEGMENT_L = "SEGMENT_L",
  SEGMENT_M = "SEGMENT_M",
  SEGMENT_N = "SEGMENT_N",
  SEGMENT_O = "SEGMENT_O",
  SEGMENT_P = "SEGMENT_P",
  SEGMENT_Q = "SEGMENT_Q",
  SEGMENT_R = "SEGMENT_R",
  SEGMENT_S = "SEGMENT_S",
  SEGMENT_T = "SEGMENT_T",
  SEGMENT_U = "SEGMENT_U",
  SEGMENT_V = "SEGMENT_V",
  SEGMENT_W = "SEGMENT_W",
  SEGMENT_X = "SEGMENT_X",
  SEGMENT_Y = "SEGMENT_Y",
  SEGMENT_Z = "SEGMENT_Z",
}

/**
 * A collection of data that represents a subtree in the support flow, to be displayed to the
 * user for selection of options to continue the support flow.
 */
export interface SupportFlowNode {
  /** A token for this specific step, to reference in branching and navigation. */
  token?: string;
  /** Text to use as the title when this node is the current node. */
  title?: string;
  /** Text to use as the button text when this step is an option. */
  button_title?: string;
  /** Button emphasis for clients to apply. Introduced in 2.45.1. */
  button_emphasis?: SupportFlowNodeButtonEmphasis;
  /**
   * If true, there's no information to display and the client should open the contact page.
   * Only relevant for the web right now.
   */
  skip_to_contact?: boolean;
  /** If this option is selected, the ClientScenario to handle this request natively. */
  action_client_scenario?: ClientScenario;
  /** If this option is selected, the URL to handle the request. Needs to have login tokens handled. */
  action_url?: string;
  /**
   * If this is true, then the below fields are not yet populated. The client
   * needs to call AdvanceSupportFlow to receive a full node.
   */
  is_stub?: boolean;
  /** Markdown text to present as the body. Not guaranteed to be populated. */
  text?: string;
  /** Additional choices available from this level of the tree. */
  child_nodes?: SupportFlowNode[];
  /**
   * Whether the contact button should be shown to the customer from this step. May be false
   * for an intermediate step where we want the customer to drill further.
   */
  contact_enabled?: boolean;
  /**
   * Whether or not a transaction should be selected/included when contacting support from this
   * node.
   */
  include_payment?: boolean;
  /** Whether or not to exclude the node from lists of recently viewed nodes on the client side */
  exclude_from_recently_viewed?: boolean;
}

export enum SupportFlowNodeButtonEmphasis {
  /** ACTION - Used for nodes where there is action to be taken. */
  ACTION = "ACTION",
  /** WARNING - Used for nodes where the customer should be made aware of something bad. */
  WARNING = "WARNING",
  /** PENDING - Used for nodes that describe something that is transient for the customer. */
  PENDING = "PENDING",
}

/**
 * This method initiates a support flow and returns data to be displayed at the next step of the
 * flow.
 */
export interface StartSupportFlowRequest {
  /** An optional identifier to start the support flow at a particular spot. */
  node_token?: string;
  /** A payment identifier which this support flow is about. */
  entity_id?: string;
}

export interface StartSupportFlowResponse {
  support_flow_node?: SupportFlowNode;
}

/**
 * This method continues or expands an existing support flow by selecting a child node. The method
 * returns data to be displayed at that level.
 */
export interface AdvanceSupportFlowRequest {
  /** The token for the current node, to track decision tree branching. */
  current_node_token?: string;
  /** The node to advance / expand. Should be one of the child nodes of the current node. */
  selected_node_token?: string;
}

export interface AdvanceSupportFlowResponse {
  support_flow_node?: SupportFlowNode;
}

/**
 * A method to find out which contact options and information should be displayed to the user based
 * on the current time as well as the current step of the support flow.
 */
export interface GetSupportContactStatusRequest {
  /** @deprecated */
  current_step_token?: string;
  current_node_token?: string;
}

export interface GetSupportContactStatusResponse {
  /** Extra information on email support, e.g. "Our current response time is 6 hours." */
  email_contact_text?: string;
  callback_enabled?: boolean;
  text_enabled?: boolean;
  instant_email_enabled?: boolean;
  chat_enabled?: boolean;
  /** Information on callback support, e.g. "We will get back to you within 15 minutes." */
  callback_contact_text?: string;
  /** Information on text support, e.g. "We will get back to you within 15 minutes." */
  text_contact_text?: string;
  /**
   * Customer-facing text explaining the contact options.
   * Deprecated in favor of title/text as of client version 3.12.
   *
   * @deprecated
   */
  header?: string;
  /** Text to use as the title of the list of contact options. */
  title?: string;
  /** Markdown text to present in addition to the contact options. */
  text?: string;
  /** Data about the contact options to be displayed. */
  contact_options?: ContactOption[];
}

export interface ContactOption {
  contact_type?: SupportContactType;
  /** If this is false, the choice should be displayed as greyed out and not clickable. */
  available?: boolean;
  /** The name for the choice, e.g. "By Phone". */
  summary_header?: string;
  /**
   * The description for the choice, e.g. "Currently unavailable due to high demand. Please try
   * later." May be null.
   */
  summary_text?: string;
  /** E.g. "Email Support" */
  contact_header?: string;
  /** Instructions to the customer, e.g. "Please be as detailed as possible." */
  contact_text?: string;
  /** Estimate of the response time e.g. 10 min */
  time_estimate?: string;
  /** If this is false, the issue description box for this contact option should be hidden. */
  include_issue_description?: boolean;
  /** Terms of service to be displayed to the customer for support of this contact type */
  contact_terms?: string;
}

export interface CreateSupportCaseRequest {
  current_node_token?: string;
  /** A payment identifier which this support case is about. */
  entity_id?: string;
  contact_type?: SupportContactType;
  /** Email address supplied by the user. Must be populated for an EMAIL contact_type. */
  email_address?: string;
  /** Phone number for the user. Must be populated for a CALLBACK contact_type. */
  phone_number?: string;
  /** The customer's support message. */
  message?: string;
  /** Acknowledgement of the customer's (implicit) acceptance of support terms */
  terms_accepted?: boolean;
}

export interface SupportCasePii {
  /** Email address supplied by the user. */
  email_address?: string;
  /** Phone number for the user. */
  phone_number?: string;
  /** The customer's support message. */
  message?: string;
}

export interface SupportOverride {
  reason?: string;
  type?: SupportOverrideType;
  /**
   * For a callback override, policy can be one of "DEFAULT", "ELIGIBLE", "INELIGIBLE".
   * For a segmentation override, policy should be set to the name of the desired segment.
   */
  policy?: string;
}

export enum SupportOverrideType {
  CALLBACK = "CALLBACK",
  SEGMENTATION = "SEGMENTATION",
  INSTANT_EMAIL = "INSTANT_EMAIL",
  TEXT = "TEXT",
}

export interface SupportScheduleRecurrence {
  id?: string;
  start_time?: string;
  end_time?: string;
  pattern?: SupportScheduleRecurrencePattern;
  /** e.g. UTC or America/Los_Angeles */
  time_zone?: string;
  queue_size?: number;
  /** Tags are out of order, next tag = 8. */
  contact_type?: SupportContactType;
}

export enum SupportScheduleRecurrencePattern {
  WEEKLY = "WEEKLY",
  WEEKDAY = "WEEKDAY",
}

export interface SupportScheduleOverride {
  id?: string;
  start_time?: string;
  end_time?: string;
  status?: SupportScheduleOverrideStatus;
  /** e.g. UTC or America/Los_Angeles */
  time_zone?: string;
  queue_size?: number;
  /** Tags are out of order, next tag = 8. */
  contact_type?: SupportContactType;
}

export enum SupportScheduleOverrideStatus {
  ENABLED = "ENABLED",
  DISABLED = "DISABLED",
}

export interface CreateSupportCaseResponse {
  /**
   * A header and text to display to the user with a successful result, e.g. "We got your message"
   * and "We'll get back you shortly at sandy@example.com".
   */
  result_header?: string;
  result_text?: string;
}

export interface ReportSupportFlowActivityRequest {
  events?: ReportSupportFlowActivityRequestSupportFlowEvent[];
}

export interface ReportSupportFlowActivityRequestSupportFlowEvent {
  /** The token for the node being opened/expanded. */
  node_token?: string;
  open_node?: ReportSupportFlowActivityRequestSupportFlowEventNodeEvent | undefined;
  expand_stub?: ReportSupportFlowActivityRequestSupportFlowEventNodeEvent | undefined;
  open_action_url?: ReportSupportFlowActivityRequestSupportFlowEventOpenActionUrlEvent | undefined;
  initiate_client_scenario?: ReportSupportFlowActivityRequestSupportFlowEventInitiateClientScenarioEvent | undefined;
  back?:
    | ReportSupportFlowActivityRequestSupportFlowEventNodeEvent
    | undefined;
  /** @deprecated */
  problem_solved?:
    | ReportSupportFlowActivityRequestSupportFlowEventNodeEvent
    | undefined;
  /** Index of the node being selected in the list. */
  position?: number;
  /** The amount of time, in milliseconds, since the last support flow event. */
  millis_since_last_event?: number;
}

export interface ReportSupportFlowActivityRequestSupportFlowEventNodeEvent {
}

export interface ReportSupportFlowActivityRequestSupportFlowEventOpenActionUrlEvent {
  action_url?: string;
}

export interface ReportSupportFlowActivityRequestSupportFlowEventInitiateClientScenarioEvent {
  client_scenario?: ClientScenario;
}

export interface ReportSupportFlowActivityResponse {
}

export interface SupportCategory {
  name?: string;
  /** A title for the category, e.g. Locate a Payment. */
  summary_title?: string;
  /**
   * Not used in support, possibly still used for Disputes web API.
   *
   * @deprecated
   */
  description_title?: string;
  /** Detailed information within the support category. */
  description?: string;
  /** Whether the support form should try to reference a payment when creating a support case. */
  include_payment?: boolean;
  /**
   * Any criteria the user must meet to have the category presented as an option, such as
   * IS_FORCED_BUSINESS, or IS_CUTOVER
   */
  criteria?: string;
  /** The name of the parent category, or null if no parent exists */
  parent_name?: string;
  visible?: boolean;
  /** Categories with lower priorities should be displayed to customers first */
  priority?: number;
  /**
   * Whether the contact button should be shown to the customer from this category. May be false
   * for an intermediate category where we want the customer to drill further.
   */
  contact_enabled?: boolean;
  /** The category that should be used to route support cases. */
  routing_category?: string;
  /** The auto-response that cases created under this support category should receive. */
  auto_response_category?: string;
  /** The client scenario that should be triggered by this support category. */
  action_client_scenario?: string;
  /** The URL that should be opened by the app when selecting this support category. */
  action_url?: string;
  /**
   * This category is being surfaced to address a current issue, which may affect presentation
   * in the app and routing of associated cases.
   */
  is_current_issue?: boolean;
  emphasis_type?: SupportCategoryEmphasisType;
  /** Whether customers may contact support via text for this category. */
  text_enabled?: boolean;
  /** Whether customers may contact support via callback for this category. */
  callback_enabled?: boolean;
  /**
   * The Salesforce queue to which cases created under this support category should be sent.
   * If null, revert to default behaviour.
   */
  routing_group?: string;
}

export enum SupportCategoryEmphasisType {
  NONE = "NONE",
  ACTION = "ACTION",
  WARNING = "WARNING",
  PENDING = "PENDING",
}

export interface Criterion {
  name?: string;
  arguments?: string[];
}

export interface Expression {
  criterion?: Criterion | undefined;
  not?: ExpressionNot | undefined;
  and?: ExpressionAnd | undefined;
}

export interface ExpressionNot {
  expression?: Expression;
}

export interface ExpressionAnd {
  left?: Expression;
  right?: Expression;
}
